import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getTiposCuenta, deleteTipoCuenta } from '../services/tipoCuentaService';
import { PlusCircle, ArrowLeft, RefreshCw, Search, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const TiposCuentaList = () => {
    const [tiposCuenta, setTiposCuenta] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { token } = useAuth();

    const fetchTiposCuenta = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getTiposCuenta(token);
            setTiposCuenta(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error al cargar tipos de cuenta:', error);
            toast.error('No se pudieron cargar los tipos de cuenta');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchTiposCuenta();
    }, [fetchTiposCuenta]);

    const handleEliminar = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar este tipo de cuenta? Esta acción no se puede deshacer.')) {
            try {
                await deleteTipoCuenta(token, id);
                toast.success('Tipo de cuenta eliminado correctamente');
                fetchTiposCuenta();
            } catch (error) {
                toast.error('No se pudo eliminar el tipo de cuenta');
            }
        }
    };

    const filteredTipos = tiposCuenta.filter(t =>
        t.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.cuentaContablePasivoNombre?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="max-w-[98%] mx-auto bg-white shadow-sm border border-gray-200 mb-10 overflow-hidden rounded-sm">
            <Toaster position="top-right" />

            {/* Header Enterprise Style (Slate) */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion#ahorros')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-lg font-semibold tracking-tight uppercase">Mantenimiento: Tipos de Cuenta (Ahorros / Vista Contable)</h1>
                </div>

                <Link
                    to="/parametrizacion/tipos-cuenta/nuevo"
                    className="flex items-center gap-1.5 transition-all rounded-sm px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm text-[0.85rem] font-bold uppercase"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVA CUENTA
                </Link>
            </div>

            {/* Controles Compactos */}
            <div className="p-3 bg-gray-50 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
                <div className="relative group">
                    <input
                        type="text"
                        placeholder="Buscar por código, nombre..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="bg-white border border-gray-300 pl-8 pr-3 py-1.5 w-72 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm shadow-sm"
                    />
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5 group-focus-within:text-blue-500" />
                </div>

                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                    {loading && <RefreshCw className="w-3 h-3 animate-spin text-blue-500" />}
                    Sincronización de Maestros de Pasivos
                </div>
            </div>

            {/* Grilla Principal con Scroll Horizontal y Sticky Columns */}
            <div className="overflow-x-auto relative">
                <table className="min-w-full text-left border-collapse table-fixed">
                    <thead>
                        <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-gray-700 text-[10px] font-bold uppercase tracking-wider">
                            <th className="px-3 py-2 w-16 text-center border-r border-gray-200 sticky left-0 bg-[#f8fafc] z-10">ID</th>
                            <th className="px-4 py-2 w-24 border-r border-gray-200 text-center">Código</th>
                            <th className="px-4 py-2 w-64 border-r border-gray-200">Denominación del Tipo de Cuenta</th>
                            <th className="px-4 py-2 w-72 border-r border-gray-200">Cuenta Contable Pasivo (Obligaciones)</th>
                            <th className="px-4 py-2 w-32 border-r border-gray-200 text-center">En Inscripción</th>
                            <th className="px-4 py-2 w-32 border-r border-gray-200 text-center">En Aprobación</th>
                            <th className="px-3 py-2 w-32 text-center sticky right-0 bg-[#f8fafc] shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)] border-l border-gray-200 z-10">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="text-[12px] text-gray-600">
                        {loading && filteredTipos.length === 0 ? (
                            <tr><td colSpan="7" className="px-4 py-8 text-center opacity-50 font-bold uppercase italic border-b border-gray-200">Cargando registros...</td></tr>
                        ) : filteredTipos.length === 0 ? (
                            <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400 font-bold uppercase italic border-b border-gray-200">No se encontraron tipos de cuenta.</td></tr>
                        ) : (
                            filteredTipos.map((t) => (
                                <tr key={t.id} className="border-b border-gray-100 hover:bg-[#f1f5f9] transition-colors group">
                                    <td className="px-3 py-2 text-center font-mono text-[11px] text-gray-400 border-r border-gray-100 sticky left-0 bg-white group-hover:bg-[#f1f5f9] z-10">{t.id}</td>
                                    <td className="px-4 py-2 text-center border-r border-gray-100 font-bold">
                                        <span className="bg-slate-100 text-slate-800 px-2 py-0.5 rounded-sm border border-slate-200 text-[10px] tracking-tight">{t.codigo}</span>
                                    </td>
                                    <td className="px-4 py-2 border-r border-gray-100 text-gray-800">
                                        <div className="font-bold uppercase tracking-tight">{t.nombre}</div>
                                        <div className="text-[9px] text-gray-400 italic truncate max-w-[240px]">{t.descripcion || 'Sin descripción adicional'}</div>
                                    </td>
                                    <td className="px-4 py-2 border-r border-gray-100">
                                        {t.cuentaContablePasivoId ? (
                                            <div className="flex items-center gap-2">
                                                <FileText className="w-3 h-3 text-blue-500 flex-shrink-0" />
                                                <div className="truncate">
                                                    <div className="text-[11px] font-black text-blue-700 leading-none">{t.cuentaContablePasivoCodigo}</div>
                                                    <div className="text-[10px] text-gray-500 uppercase font-medium mt-0.5 truncate">{t.cuentaContablePasivoNombre}</div>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-[10px] text-gray-400 italic font-medium uppercase tracking-tighter">Sin vinculación contable</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 border-r border-gray-100 text-center">
                                        <span className={`px-2 py-1 rounded-full text-[10px] font-black ${t.inscripcion === 'S' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-400 border border-gray-200'}`}>
                                            {t.inscripcion === 'S' ? 'SÍ' : 'NO'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 border-r border-gray-100 text-center">
                                        <span className={`px-2 py-1 rounded-full text-[10px] font-black ${t.aprobacion === 'S' ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-gray-100 text-gray-400 border border-gray-200'}`}>
                                            {t.aprobacion === 'S' ? 'SÍ' : 'NO'}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-center sticky right-0 bg-white shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.1)] border-l border-gray-200 group-hover:bg-[#f1f5f9] z-10">
                                        <div className="flex justify-center items-center gap-2 font-semibold text-xs tracking-tight">
                                            <button
                                                onClick={() => navigate(`/parametrizacion/tipos-cuenta/${t.id}/editar`)}
                                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                            >
                                                EDITAR
                                            </button>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                onClick={() => handleEliminar(t.id)}
                                                className="text-red-500 hover:text-red-700 transition-colors"
                                            >
                                                ELIMINAR
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Footer de Estado Enterprise */}
            <div className="p-3 bg-gray-50 flex justify-between items-center border-t border-gray-200 text-[0.875rem] text-gray-500 uppercase font-bold tracking-tight">
                <div>
                    Total registros: <span className="text-gray-700">{filteredTipos.length}</span>
                </div>
                <div className="flex gap-4">
                    <span className="text-[10px] text-gray-300">Auditoría: Activo</span>
                </div>
            </div>
        </div>
    );
};

export default TiposCuentaList;
